import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Activity, Database, Clock, HardDrive, Server, 
  RefreshCw, Copy, Check, AlertCircle, CheckCircle2,
  Calendar, Layers, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

export default function SystemStatusPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/system-status');
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleCopyDiagnostics = async () => {
    if (!status) return;
    
    const diagnostics = `
System Diagnostics Report
=========================
Version: ${status.version}
Time: ${status.time}
Timezone: ${status.timezone}
Database: ${status.database}

Scheduler Status:
- Running: ${status.scheduler?.running ?? 'unknown'}
- Jobs: ${status.scheduler?.jobs?.length ?? 0}

Last Job Run: ${status.job_runs?.[0]?.job_name ?? 'None'} - ${status.job_runs?.[0]?.status ?? 'N/A'}

Storage:
- Files: ${status.storage?.file_count ?? 0}
- Size: ${status.storage?.total_size_mb ?? 0} MB

Data Counts:
- Branches: ${status.counts?.branches ?? 0}
- Rooms: ${status.counts?.rooms ?? 0}
- Assets: ${status.counts?.assets ?? 0}
- Users: ${status.counts?.users ?? 0}
- Work Requests: ${status.counts?.work_requests ?? 0}
- Work Orders: ${status.counts?.work_orders ?? 0}
    `.trim();

    try {
      await navigator.clipboard.writeText(diagnostics);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const StatusBadge = ({ ok }) => (
    <Badge variant={ok ? 'default' : 'destructive'} className={ok ? 'bg-green-500/10 text-green-500 border-green-500/20' : ''}>
      {ok ? <CheckCircle2 className="w-3 h-3 me-1" /> : <AlertCircle className="w-3 h-3 me-1" />}
      {ok ? 'OK' : 'Error'}
    </Badge>
  );

  // Helper to determine scheduler status display
  const getSchedulerStatus = () => {
    if (!status?.scheduler) return { ok: false, label: t('systemStatus.schedulerInactive') };
    if (status.scheduler.running === true) return { ok: true, label: 'OK' };
    if (status.scheduler.running === false) return { ok: false, label: t('systemStatus.schedulerInactive'), isInactive: true };
    return { ok: false, label: t('systemStatus.schedulerInactive'), isInactive: true };
  };

  const SchedulerBadge = () => {
    const schedulerStatus = getSchedulerStatus();
    if (schedulerStatus.isInactive) {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
          <AlertCircle className="w-3 h-3 me-1" />
          {schedulerStatus.label}
        </Badge>
      );
    }
    return (
      <Badge variant={schedulerStatus.ok ? 'default' : 'destructive'} className={schedulerStatus.ok ? 'bg-green-500/10 text-green-500 border-green-500/20' : ''}>
        {schedulerStatus.ok ? <CheckCircle2 className="w-3 h-3 me-1" /> : <AlertCircle className="w-3 h-3 me-1" />}
        {schedulerStatus.label}
      </Badge>
    );
  };

  return (
    <div data-testid="system-status-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight">
            {t('systemStatus.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('systemStatus.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={loadStatus} disabled={loading}>
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyDiagnostics} disabled={!status}>
            {copied ? <Check className="w-4 h-4 me-2" /> : <Copy className="w-4 h-4 me-2" />}
            {t('systemStatus.copyDiagnostics')}
          </Button>
        </div>
      </div>

      {loading && !status ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin me-2" />
          {t('common.loading')}
        </div>
      ) : status ? (
        <>
          {/* Health Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Server className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('systemStatus.apiHealth')}</p>
                  <StatusBadge ok={status.status === 'ok'} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Database className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('systemStatus.database')}</p>
                  <StatusBadge ok={status.database === 'ok'} />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('systemStatus.scheduler')}</p>
                  <SchedulerBadge />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <HardDrive className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('systemStatus.storage')}</p>
                  <p className="text-lg font-bold text-foreground">{status.storage?.file_count} {t('systemStatus.files')}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* System Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Version & Time */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Activity className="w-5 h-5 text-brand" />
                  {t('systemStatus.systemInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">{t('systemStatus.version')}</span>
                  <span className="font-mono text-foreground">{status.version}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">{t('systemStatus.serverTime')}</span>
                  <span className="font-mono text-foreground">{formatDate(status.time)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">{t('systemStatus.timezone')}</span>
                  <span className="font-mono text-foreground">{status.timezone}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">{t('systemStatus.storageUsed')}</span>
                  <span className="font-mono text-foreground">{status.storage?.total_size_mb} MB</span>
                </div>
                {status.storage?.disk_free_mb && (
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">{t('systemStatus.diskFree')}</span>
                    <span className="font-mono text-foreground">{Math.round(status.storage.disk_free_mb / 1024 * 10) / 10} GB / {Math.round(status.storage.disk_total_mb / 1024 * 10) / 10} GB</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">{t('systemStatus.storageBackend')}</span>
                  <span className="font-mono text-foreground">{status.storage?.backend || 'local'}</span>
                </div>
                {status.storage?.warning && (
                  <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2" data-testid="storage-warning">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {status.storage.warning}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Data Counts */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Layers className="w-5 h-5 text-brand" />
                  {t('systemStatus.dataCounts')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(status.counts || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 px-3 bg-muted/30 rounded">
                      <span className="text-muted-foreground capitalize">{key.replace('_', ' ')}</span>
                      <span className="font-bold text-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Job Runs */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-barlow flex items-center gap-2">
                <Calendar className="w-5 h-5 text-brand" />
                {t('systemStatus.recentJobs')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {status.job_runs?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('systemStatus.jobName')}</th>
                        <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('systemStatus.startedAt')}</th>
                        <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('common.status')}</th>
                        <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('systemStatus.processed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {status.job_runs.map((job, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="px-4 py-2.5 font-medium text-foreground">{job.job_name}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{formatDate(job.started_at)}</td>
                          <td className="px-4 py-2.5">
                            <Badge variant={job.status === 'completed' ? 'default' : 'destructive'} 
                                   className={job.status === 'completed' ? 'bg-green-500/10 text-green-500 border-green-500/20' : ''}>
                              {job.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">{job.records_processed ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">{t('systemStatus.noJobs')}</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Email Provider Test */}
      <TestEmailCard />
    </div>
  );
}

function TestEmailCard() {
  const { t } = useTranslation();
  const [emailStatus, setEmailStatus] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/admin/email/status').then(({ data }) => setEmailStatus(data)).catch(() => {});
  }, []);

  const handleSendTest = async () => {
    setSending(true);
    setResult(null);
    try {
      const payload = recipient.trim() ? { recipient_email: recipient.trim() } : {};
      const { data } = await api.post('/admin/email/test', payload);
      setResult(data);
      if (data.success) {
        toast.success(t('systemStatus.testEmailSent'));
      } else {
        toast.error(data.error || t('systemStatus.testEmailFailed'));
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-card border-border shadow-sm" data-testid="test-email-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-500" />
          {t('systemStatus.emailProvider')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('systemStatus.provider')}</p>
            <p className="text-sm font-medium text-foreground">{emailStatus?.provider || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">{t('common.status')}</p>
            {emailStatus?.configured ? (
              <Badge className="bg-green-500/10 text-green-500">{t('systemStatus.configured')}</Badge>
            ) : (
              <Badge className="bg-amber-500/10 text-amber-500">{t('systemStatus.notConfigured')}</Badge>
            )}
          </div>
        </div>
        <Separator />
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground">{t('systemStatus.testRecipient')}</label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t('systemStatus.testRecipientPlaceholder')}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="test-email-recipient"
            />
          </div>
          <Button
            onClick={handleSendTest}
            disabled={sending}
            data-testid="send-test-email-btn"
            className="shrink-0"
          >
            {sending ? t('common.loading') : t('systemStatus.sendTestEmail')}
          </Button>
        </div>
        {result && (
          <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`} data-testid="test-email-result">
            <p className="font-medium">{result.success ? 'Email sent successfully!' : 'Email send failed'}</p>
            <p className="text-xs mt-1">Provider: {result.provider} | Status: {result.status} | To: {result.recipient}</p>
            {result.error && <p className="text-xs mt-1">Error: {result.error}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
